import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { User, UserClaims } from "../models/shared/User";
import { useFirestore, useUser as useReactFireUser } from 'reactfire'
import { doc, getDoc, setDoc } from "firebase/firestore";
import { COLLECTIONS } from "../utils/shared/constants";
import { fromFirebaseDoc } from "../utils/shared/firebase";
import useQueryParam from "../hooks/useQueryParams";
import { getSuperUserInfo, postLoginVerify } from "../utils/api";
import { TextProvider } from "./TextContext";
import { getBrowserLanguage } from "../utils/presentation";
import { excludeFromAnalytics, identifyUser } from "../utils/analytics";



interface UserContext {
    loaded: boolean;
    user: User | null;
    claims: UserClaims;
    getToken: () => Promise<string>;
    refetchClaims: () => Promise<void>;
    updateUser: (update: Partial<User>) => Promise<void>;
}

const emptyContext: UserContext = {
    loaded: false,
    user: null,
    claims: { ownerOf: [], userOf: [], userWorkerOf: [] },
    getToken: async () => '',
    refetchClaims: async () => { },
    updateUser: async () => { }
};

const userContext = createContext<UserContext>(emptyContext);

export const useUser = () => useContext(userContext);

interface UserContextProviderProps {
    children: React.ReactNode;
}

interface UserPart {
    loaded: boolean;
    user: User | null;
    superUser?: string;
}

export const UserContextProvider = (props: UserContextProviderProps) => {
    const [userPart, setUserPart] = useState<UserPart>({ loaded: false, user: null });
    const [claims, setClaims] = useState<UserClaims>({ ownerOf: [], userOf: [], userWorkerOf: [] });
    const { superUser } = useQueryParam();

    const { data: userReactFire } = useReactFireUser();
    const firestore = useFirestore();

    useEffect(() => {
        if (!userReactFire?.uid) {
            return;
        }

        const asyncEffect = async () => {

            if (superUser) {
                const token = await userReactFire.getIdToken();
                const { user, claims } = await getSuperUserInfo(token, superUser);

                if (!user || !claims?.ownerOf?.length) {
                    console.error('Superuser not found', superUser);
                    return;
                }

                excludeFromAnalytics();
                setUserPart({ loaded: true, user, superUser: superUser });
                setClaims(claims);
                return;
            }

            let tokenInfo = await userReactFire.getIdTokenResult();
            const allOrgs = [...(tokenInfo.claims.ownerOf || []), ...(tokenInfo.claims.userOf || []), ...(tokenInfo.claims.userWorkerOf || [])];
            if (allOrgs.length === 0) {
                console.warn('User does not belong to any organization yet');
                const userToken = await userReactFire.getIdToken();
                await postLoginVerify(userToken, getBrowserLanguage());

                tokenInfo = await userReactFire.getIdTokenResult(true);
            }


            const claims: UserClaims = {
                superAdmin: tokenInfo.claims.superAdmin,
                ownerOf: tokenInfo.claims.ownerOf || [],
                userOf: tokenInfo.claims.userOf || [],
                userWorkerOf: tokenInfo.claims.userWorkerOf || [],
            }

            if (claims.superAdmin) {
                excludeFromAnalytics();
            } else {
                identifyUser(userReactFire.uid, userReactFire.email || '');
            }

            const docRef = doc(firestore, `${COLLECTIONS.USERS}/${userReactFire?.uid}`);
            const docSnap = await getDoc(docRef);
            const existingUser = fromFirebaseDoc<User>(docSnap);

            let newUserUpdate: Partial<User> = {
                uid: userReactFire.uid,
                email: userReactFire.email || '',
                lastSeenAt: new Date()
            }
            let skipUpdate = false;
            if (existingUser) {
                setUserPart({ loaded: true, user: existingUser });
                skipUpdate = existingUser.lastSeenAt && (new Date().getTime() - existingUser.lastSeenAt.getTime()) <= 5 * 60 * 1000;
            } else {
                newUserUpdate.createdAt = new Date();
                setUserPart({ loaded: true, user: newUserUpdate as User });
            }
            setClaims(claims);
            if (!skipUpdate) {
                await setDoc(docRef, newUserUpdate, { merge: true });
            }
        }
        asyncEffect();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userReactFire, firestore]);

    const updateUser = useCallback(async (update: Partial<User>) => {
        const uid = userPart.user?.uid;
        if (!uid || !userPart.user) {
            return;
        }
        setUserPart({ ...userPart, user: { ...userPart.user, ...update } });

        const userRef = doc(firestore, `${COLLECTIONS.USERS}/${uid}`);
        await setDoc(userRef, update, { merge: true });
    }, [firestore, userPart]);

    const refetchClaims = useCallback(async () => {
        if (!userReactFire?.uid) {
            return;
        }

        const tokenInfo = await userReactFire.getIdTokenResult(true);
        const claims: UserClaims = {
            superAdmin: tokenInfo.claims.superAdmin,
            ownerOf: tokenInfo.claims.ownerOf || [],
            userOf: tokenInfo.claims.userOf || [],
            userWorkerOf: tokenInfo.claims.userWorkerOf || [],
        }
        setClaims(claims);
    }, [userReactFire])
    console.log('Rerendreing user...');

    return (
        <userContext.Provider value={{
            ...userPart,
            claims,
            getToken: () => userReactFire?.getIdToken() || Promise.resolve(''),
            refetchClaims,
            updateUser
        }}>
            <TextProvider language={userPart.user?.settings?.language || getBrowserLanguage()}>
                {userPart.superUser && <div className=" center bg-danger p-1 text-bright">Superuser: {userPart.superUser}</div>}
                {props.children}
            </TextProvider>
        </userContext.Provider>
    )
}